import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Landmark, Link2Off, ShieldCheck, CheckCircle2, Clock, ShieldOff, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, LogoMark } from "@/components/Logo";
import PublicPay from "@/components/PublicPay";
import ReportSheet from "@/components/ReportSheet";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtNaira, fmtDate } from "@/lib/format";

function Wrap({ children }) {
  return (
    <div className="debii-shell min-h-screen px-6 py-8">
      <div className="flex justify-center"><Logo size={30} /></div>
      <div className="mt-8 max-w-sm mx-auto w-full animate-rise">{children}</div>
    </div>
  );
}

function TryDebii() {
  return (
    <div className="mt-6 rounded-3xl bg-primary text-primary-foreground p-6" data-testid="share-try-debii-card">
      <p className="font-display text-lg font-bold">Keep track of money people owe you too.</p>
      <p className="mt-1 text-sm opacity-80">Debii makes it simple to remember who owes you and when.</p>
      <Button
        data-testid="share-try-debii"
        onClick={() => (window.location.href = "/welcome")}
        className="mt-4 w-full h-12 rounded-xl bg-white text-primary hover:bg-white/90 text-base"
      >
        Try Debii Free
      </Button>
    </div>
  );
}

function AckBadge() {
  return (
    <div className="mt-4 flex items-center gap-2 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="share-acknowledged">
      <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0" />
      <div>
        <p className="font-display font-bold text-emerald-800 text-sm">Acknowledged ✓</p>
        <p className="text-xs text-emerald-700">You've acknowledged this debt.</p>
      </div>
    </div>
  );
}

// Secure debt view opened from a reminder link — reflects the live debt/payment state.
export default function PublicDebtView() {
  const { token } = useParams();
  const [showPay, setShowPay] = useState(false);
  const [acking, setAcking] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [resolved, setResolved] = useState(null); // reported | blocked

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["debt-share", token],
    queryFn: async () => (await api.get(`/d/${token}`)).data,
    retry: false,
  });

  const acknowledge = async () => {
    setAcking(true);
    try {
      await api.post(`/d/${token}/acknowledge`);
      await refetch();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAcking(false);
    }
  };

  if (isLoading) {
    return <Wrap><div className="flex justify-center py-16"><div className="animate-pop"><LogoMark size={48} /></div></div></Wrap>;
  }
  if (error || !data) {
    return (
      <Wrap>
        <div className="text-center py-10" data-testid="share-invalid">
          <Link2Off className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">This link isn't valid or has expired.</p>
        </div>
      </Wrap>
    );
  }

  // Report / block terminal states take precedence over everything else.
  const isBlocked = resolved === "blocked" || data.blocked;
  const isReported = resolved === "reported" || data.reported;
  if (isBlocked || isReported) {
    return (
      <Wrap>
        <div className="text-center py-6" data-testid={isBlocked ? "share-blocked" : "share-reported"}>
          <div className="mx-auto h-20 w-20 rounded-full bg-amber-100 flex items-center justify-center animate-pop">
            {isBlocked ? <ShieldOff className="h-11 w-11 text-amber-700" /> : <Flag className="h-11 w-11 text-amber-700" />}
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold text-foreground">
            {isBlocked ? "Request blocked" : "Report received"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isBlocked
              ? "You've blocked this sender. They can no longer ask you to acknowledge, dispute or repay this request."
              : "Thanks — our team will review this request. You've already reported it."}
          </p>
          <TryDebii />
        </div>
      </Wrap>
    );
  }

  if (showPay) {
    return <PublicPay basePath={`/d/${token}`} creditor={data.creditor_name} onClose={async () => { await refetch(); setShowPay(false); }} />;
  }

  const cur = data.currency;
  const settled = data.status === "paid";
  const pending = data.pending_payment;
  const canPay = data.repayment && data.repayment.available;

  return (
    <Wrap>
      <div data-testid="public-debt-view">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{data.person_name}</span>, here's your debt with {data.creditor_name}.
        </p>

        {/* Balance card */}
        <div className="mt-4 rounded-3xl bg-card border border-border p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Outstanding balance</p>
          <p data-testid="share-outstanding" className="font-display text-4xl font-extrabold tabular text-foreground">
            {fmtNaira(data.outstanding_amount, cur)}
          </p>
          <div className="mt-4 border-t border-border pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Original amount</span>
              <span className="text-sm font-semibold text-foreground">{fmtNaira(data.original_amount, cur)}</span>
            </div>
            {data.due_date && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Due date</span>
                <span className="text-sm font-semibold text-foreground">{fmtDate(data.due_date)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Acknowledgement status */}
        {data.acknowledged && !settled && <AckBadge />}

        {/* State-specific action area */}
        {settled ? (
          <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="share-settled">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-700" />
            <p className="mt-1 font-display font-bold text-emerald-800">Settled ✓</p>
            <p className="text-sm text-emerald-700 mt-0.5">This debt has been fully repaid.</p>
          </div>
        ) : pending ? (
          <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-5 text-center" data-testid="share-pending">
            <Clock className="mx-auto h-8 w-8 text-amber-700" />
            <p className="mt-1 font-display font-bold text-amber-800">Payment awaiting confirmation</p>
            <p className="text-sm text-amber-700 mt-1">
              {fmtNaira(pending.amount, cur)} has been reported as paid. Your balance will update once {data.creditor_name} confirms receipt.
            </p>
          </div>
        ) : !data.acknowledged ? (
          <div className="mt-4">
            <Button data-testid="share-acknowledge-btn" disabled={acking} onClick={acknowledge} className="w-full h-12 rounded-xl text-base">
              {acking ? "Saving…" : "I acknowledge this debt"}
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground">Confirm this debt is correct before you repay it.</p>
          </div>
        ) : canPay ? (
          <>
            <Button data-testid="share-pay-debt" onClick={() => setShowPay(true)} className="mt-4 w-full h-12 rounded-xl text-base">
              <Landmark className="h-5 w-5" /> Pay this debt
            </Button>
            <p className="mt-3 text-[11px] text-muted-foreground text-center">
              Debii never receives or holds your money. Payment is made directly to {data.creditor_name}.
            </p>
          </>
        ) : (
          <p data-testid="share-no-repay" className="mt-4 text-center text-xs text-muted-foreground">
            {data.creditor_name} hasn't added repayment details yet.
          </p>
        )}

        {/* Try Debii Free — always available on this public experience */}
        <TryDebii />

        <div className="mt-6 flex items-start gap-2.5 rounded-2xl bg-secondary p-4">
          <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            This is a secure Debii record shared with you. You don't need an account to view, acknowledge or repay it.
          </p>
        </div>

        <button data-testid="share-report-link" onClick={() => setShowReport(true)}
          className="mt-4 w-full text-center text-xs text-muted-foreground underline underline-offset-2">
          Report this request or block the sender
        </button>
        <ReportSheet open={showReport} onOpenChange={setShowReport} basePath={`/d/${token}`} onResolved={setResolved} />
      </div>
    </Wrap>
  );
}
